import { ref, toRaw } from 'vue'
import { defineStore } from 'pinia'

import { getLocalizedItems, updateLocaleAsync, getTranslatedItems } from '@/plugins/i18n'
import { useGetOrFetchAsync, useCheckLastUpdate } from '@/composables/store'

import { portalApi } from '@/apis/portal.api'
import { menuRoutesEnum } from '@/router'

export const initPortalStoreAsync = async(isAuthenticated) => {
  const store = usePortalStore()
  const promises = [
    store.fetchVersionAsync(),
    store.fetchSettingsAsync(),
  ]
  if (isAuthenticated) {
    await useCheckLastUpdate(store, promises)
  }
  await Promise.all(promises)
}

export const usePortalStore = defineStore('portal', () => {
  const api = portalApi

  const appointmentNotifyEnum = Object.freeze({
    SMS: 2 ** 0,
    WHATSAPP: 2 ** 1,
  })

  const attachmentCategoryEnum = Object.freeze({
    CONTRACT: 0,
    SALE_DOCUMENT: 1,
    GARAGE_DOCUMENT: 2,
    ORDER: 3,
    SUBJECT: 4,
    VEHICLE: 5,
  })
  const attachmentSubcategoryEnum = Object.freeze({
    DOCUMENT: 0,
    SUBJECT: 1,
    SOLD_VEHICLE: 2,
    TRADE_IN_VEHICLE: 3,
    VEHICLE: 4,
  })
  const attachmentTagEnum = Object.freeze({
    REFERENCE_DOCUMENT: 0,
    PRIVACY_AUTHORIZATION: 1,
    OTHER: 2,
    TAX_CODE: 3,
    IDENTITY_DOCUMENT: 4,
    LOYALTY_CARD: 5,
    COMPLIANCE: 6,
    VEHICLE_PHOTO: 7,
    DAMAGE_PHOTO: 8,
    WARRANTY: 9,
    VEHICLE_REGISTRATION_DOCUMENT: 10,
    HOMOLOGATION: 11,
    OPTIONAL_EQUIPMENT: 12,
    CONTRACT: 13,
  })
  const attachmentCategoryMap = Object.freeze({
    [attachmentCategoryEnum.CONTRACT]: [attachmentSubcategoryEnum.DOCUMENT, attachmentSubcategoryEnum.SUBJECT, attachmentSubcategoryEnum.SOLD_VEHICLE, attachmentSubcategoryEnum.TRADE_IN_VEHICLE],
    [attachmentCategoryEnum.SALE_DOCUMENT]: [attachmentSubcategoryEnum.DOCUMENT, attachmentSubcategoryEnum.SUBJECT],
    [attachmentCategoryEnum.GARAGE_DOCUMENT]: [attachmentSubcategoryEnum.DOCUMENT, attachmentSubcategoryEnum.SUBJECT, attachmentSubcategoryEnum.VEHICLE],
    [attachmentCategoryEnum.ORDER]: [attachmentSubcategoryEnum.DOCUMENT, attachmentSubcategoryEnum.SUBJECT],
    [attachmentCategoryEnum.SUBJECT]: [attachmentSubcategoryEnum.SUBJECT],
    [attachmentCategoryEnum.VEHICLE]: [attachmentSubcategoryEnum.VEHICLE, attachmentSubcategoryEnum.SUBJECT],
  })
  const attachmentTagMap = Object.freeze({
    [attachmentSubcategoryEnum.DOCUMENT]: [attachmentTagEnum.REFERENCE_DOCUMENT, attachmentTagEnum.CONTRACT, attachmentTagEnum.OTHER],
    [attachmentSubcategoryEnum.SUBJECT]: [attachmentTagEnum.PRIVACY_AUTHORIZATION, attachmentTagEnum.TAX_CODE, attachmentTagEnum.CONTRACT, attachmentTagEnum.IDENTITY_DOCUMENT, attachmentTagEnum.LOYALTY_CARD, attachmentTagEnum.OTHER],
    [attachmentSubcategoryEnum.SOLD_VEHICLE]: [attachmentTagEnum.COMPLIANCE, attachmentTagEnum.CONTRACT, attachmentTagEnum.VEHICLE_PHOTO, attachmentTagEnum.DAMAGE_PHOTO, attachmentTagEnum.WARRANTY, attachmentTagEnum.VEHICLE_REGISTRATION_DOCUMENT, attachmentTagEnum.HOMOLOGATION, attachmentTagEnum.OPTIONAL_EQUIPMENT, attachmentTagEnum.OTHER],
    [attachmentSubcategoryEnum.TRADE_IN_VEHICLE]: [attachmentTagEnum.COMPLIANCE, attachmentTagEnum.CONTRACT, attachmentTagEnum.VEHICLE_PHOTO, attachmentTagEnum.DAMAGE_PHOTO, attachmentTagEnum.WARRANTY, attachmentTagEnum.VEHICLE_REGISTRATION_DOCUMENT, attachmentTagEnum.HOMOLOGATION, attachmentTagEnum.OPTIONAL_EQUIPMENT, attachmentTagEnum.OTHER],
    [attachmentSubcategoryEnum.VEHICLE]: [attachmentTagEnum.COMPLIANCE, attachmentTagEnum.CONTRACT, attachmentTagEnum.VEHICLE_PHOTO, attachmentTagEnum.DAMAGE_PHOTO, attachmentTagEnum.WARRANTY, attachmentTagEnum.VEHICLE_REGISTRATION_DOCUMENT, attachmentTagEnum.HOMOLOGATION, attachmentTagEnum.OPTIONAL_EQUIPMENT, attachmentTagEnum.OTHER],
  })

  const discountPropertyEnum = Object.freeze({
    MIN_MAX: 2 ** 0,
    DISCOUNT_CATEGORY: 2 ** 1,
    BRAND: 2 ** 2,
    URGENCY: 2 ** 3,
    DISCOUNT_LIST: 2 ** 4,
    CUSTOMER: 2 ** 5,
  })
  const discountPriorityMasks = [
    // Customer
    discountPropertyEnum.CUSTOMER | discountPropertyEnum.URGENCY | discountPropertyEnum.BRAND | discountPropertyEnum.DISCOUNT_CATEGORY | discountPropertyEnum.MIN_MAX,
    discountPropertyEnum.CUSTOMER | discountPropertyEnum.URGENCY | discountPropertyEnum.BRAND | discountPropertyEnum.DISCOUNT_CATEGORY,
    discountPropertyEnum.CUSTOMER | discountPropertyEnum.URGENCY | discountPropertyEnum.BRAND | discountPropertyEnum.MIN_MAX,
    discountPropertyEnum.CUSTOMER | discountPropertyEnum.URGENCY | discountPropertyEnum.BRAND,
    discountPropertyEnum.CUSTOMER | discountPropertyEnum.URGENCY | discountPropertyEnum.MIN_MAX,
    discountPropertyEnum.CUSTOMER | discountPropertyEnum.URGENCY,
    discountPropertyEnum.CUSTOMER | discountPropertyEnum.BRAND | discountPropertyEnum.DISCOUNT_CATEGORY | discountPropertyEnum.MIN_MAX,
    discountPropertyEnum.CUSTOMER | discountPropertyEnum.BRAND | discountPropertyEnum.DISCOUNT_CATEGORY,
    discountPropertyEnum.CUSTOMER | discountPropertyEnum.BRAND | discountPropertyEnum.MIN_MAX,
    discountPropertyEnum.CUSTOMER | discountPropertyEnum.BRAND,
    discountPropertyEnum.CUSTOMER | discountPropertyEnum.MIN_MAX,
    discountPropertyEnum.CUSTOMER,

    // Discount list
    discountPropertyEnum.DISCOUNT_LIST | discountPropertyEnum.URGENCY | discountPropertyEnum.BRAND | discountPropertyEnum.DISCOUNT_CATEGORY | discountPropertyEnum.MIN_MAX,
    discountPropertyEnum.DISCOUNT_LIST | discountPropertyEnum.URGENCY | discountPropertyEnum.BRAND | discountPropertyEnum.DISCOUNT_CATEGORY,
    discountPropertyEnum.DISCOUNT_LIST | discountPropertyEnum.URGENCY | discountPropertyEnum.BRAND | discountPropertyEnum.MIN_MAX,
    discountPropertyEnum.DISCOUNT_LIST | discountPropertyEnum.URGENCY | discountPropertyEnum.BRAND,
    discountPropertyEnum.DISCOUNT_LIST | discountPropertyEnum.URGENCY | discountPropertyEnum.MIN_MAX,
    discountPropertyEnum.DISCOUNT_LIST | discountPropertyEnum.URGENCY,
    discountPropertyEnum.DISCOUNT_LIST | discountPropertyEnum.BRAND | discountPropertyEnum.DISCOUNT_CATEGORY | discountPropertyEnum.MIN_MAX,
    discountPropertyEnum.DISCOUNT_LIST | discountPropertyEnum.BRAND | discountPropertyEnum.DISCOUNT_CATEGORY,
    discountPropertyEnum.DISCOUNT_LIST | discountPropertyEnum.BRAND | discountPropertyEnum.MIN_MAX,
    discountPropertyEnum.DISCOUNT_LIST | discountPropertyEnum.BRAND,
    discountPropertyEnum.DISCOUNT_LIST | discountPropertyEnum.MIN_MAX,
    discountPropertyEnum.DISCOUNT_LIST,

    // Generic
    discountPropertyEnum.URGENCY | discountPropertyEnum.BRAND | discountPropertyEnum.DISCOUNT_CATEGORY | discountPropertyEnum.MIN_MAX,
    discountPropertyEnum.URGENCY | discountPropertyEnum.BRAND | discountPropertyEnum.DISCOUNT_CATEGORY,
    discountPropertyEnum.URGENCY | discountPropertyEnum.BRAND | discountPropertyEnum.MIN_MAX,
    discountPropertyEnum.URGENCY | discountPropertyEnum.BRAND,
    discountPropertyEnum.BRAND | discountPropertyEnum.DISCOUNT_CATEGORY | discountPropertyEnum.MIN_MAX,
    discountPropertyEnum.BRAND | discountPropertyEnum.DISCOUNT_CATEGORY,
    discountPropertyEnum.BRAND | discountPropertyEnum.MIN_MAX,
    discountPropertyEnum.BRAND,
  ]
  const catalogs = ref([])
  const customerPrivacyModules = ref([])
  const subjectValidationTypesEnum = Object.freeze({
    NONE: 0,
    PARTIAL: 1,
    PRIVACY: 2,
  })
  const documentStatusesEnum = Object.freeze({
    CLOSED: 0,
    OPEN: 1
  })
  const documentProgressStatusesEnum = Object.freeze({
    PLANNED: 0,
    OPEN: 1,
    IN_PROGRESS: 2,
    PENDING: 3,
    CANCELLED: 4,
    COMPLETED: 5,
    INVOICED: 6,
  })
  const documentGenerateEnum = Object.freeze({
    WORKSHEET: 0,
    IMMEDIATE_INVOICE: 1,
    DEFERRED_INVOICE: 2,
    CORRESPONDENT: 3,
    CREDIT_NOTE: 4,
  })
  const documentGeneratingEnum = Object.freeze({
    SCHEDULER: 0,
    QUOTE: 1,
    WORKSHEET: 2,
    DDT: 3,
    DEFERRED_INVOICE: 4,
    IMMEDIATE_INVOICE: 5,
  })
  const documentStatuses = ref([
    { id: documentStatusesEnum.CLOSED, name: 'common.documentStatuses.closed' },
    { id: documentStatusesEnum.OPEN, name: 'common.documentStatuses.open' },
  ])
  const documentTypesEnum = Object.freeze({
    SALE_CORRESPONDENT: 0,
    SALE_DDT: 1,
    SALE_INVOICE: 2,
    SALE_IMMEDIATE_INVOICE: 3,
    SALE_FISCAL_RECEIPT: 4,
    SALE_CREDIT_NOTE: 5,
    GARAGE_QUOTE: 6,
    GARAGE_WORKSHEET: 7,
    GARAGE_EVENT: 8,
    ORDER_SUPPLIER_ORDER: 9,
    CLIPBOARD: 10,
  })
  const documentTypePageMap = Object.freeze({
    [documentTypesEnum.SALE_CORRESPONDENT]: menuRoutesEnum.SALE_DIRECT,
    [documentTypesEnum.SALE_DDT]: menuRoutesEnum.SALE_COMMERCIAL,
    [documentTypesEnum.SALE_INVOICE]: menuRoutesEnum.SALE_INVOICES,
    [documentTypesEnum.SALE_IMMEDIATE_INVOICE]: menuRoutesEnum.SALE_INVOICES,
    [documentTypesEnum.SALE_CREDIT_NOTE]: menuRoutesEnum.SALE_INVOICES,
    // [documentTypesEnum.SALE_FISCAL_RECEIPT]: menuRoutesEnum.SALE_COMMERCIAL, --- US 623 https://dev.azure.com/softway-it/ArcoWeb/_workitems/edit/623
    [documentTypesEnum.GARAGE_QUOTE]: menuRoutesEnum.QUOTES,
    [documentTypesEnum.GARAGE_WORKSHEET]: menuRoutesEnum.WORKSHEETS,
    [documentTypesEnum.GARAGE_EVENT]: menuRoutesEnum.GARAGE_EVENT_SCHEDULER,
    [documentTypesEnum.ORDER_SUPPLIER_ORDER]: menuRoutesEnum.SUPPLIER_ORDERS,
  })
  const electronicInvoiceFamiliesEnum = Object.freeze({
    NOT_GENERABLE: 0,
    TO_BE_GENERATED: 1,
    GENERATED: 2,
    SENT: 3,
    ERROR: 4,
    NOT_DELIVERED: 5,
    DELIVERED: 6,
    LOCKED: 7,
  })
  const shippingCostTypeEnum = Object.freeze({
    PREPAID: 1,
    COLLECT: 2,
  })
  const electronicInvoiceProgressStatuses = ref([])
  const electronicInvoiceSdiDocumentTypes = ref([])
  const electronicInvoiceEditableFamilies = [
    electronicInvoiceFamiliesEnum.LOCKED,
    electronicInvoiceFamiliesEnum.NOT_GENERABLE,
    electronicInvoiceFamiliesEnum.ERROR,
    electronicInvoiceFamiliesEnum.TO_BE_GENERATED,
  ]
  const electronicInvoiceProgressStatusesEnum = Object.freeze({
    NOT_GENERABLE: 0,
    TO_BE_GENERATED: 5,
    GENERATED_XML: 6,
    INTERNAL_TRANSMISSION_ERROR: 7,
    SENT_TO_CONNECTOR: 10,
    SENT_TO_SDI: 11,
    NOT_YET_DELIVERED: 12,
    REPROCESSED: 13,
    PROCESSING_SDI: 20,
    CONNECTOR_GENERIC_ERROR: 21,
    REJECTED_SDI: 31,
    REJECTED_PA: 32,
    SDI_GENERIC_ERROR: 40,
    FAILED_DELIVERY: 60,
    TAX_DRAWER: 61,
    DELIVERED: 80,
    MANUAL_DELIVERY: 81,
    DELIVERED_ACCEPTED_PA: 82,
    MANUAL_UPLOAD: 83,
    DELIVERED_EXPIRED_PA_TERMS: 84,
    MANUAL_LOCK: 90,
    NUMBERING_LOCK: 91,
    REASON_LOCK: 92,
    COUNTRY_LOCK: 93,
  })
  const electronicInvoiceProgressStatusesMap = Object.freeze({
    [electronicInvoiceProgressStatusesEnum.NOT_GENERABLE]: electronicInvoiceFamiliesEnum.NOT_GENERABLE,
    [electronicInvoiceProgressStatusesEnum.TO_BE_GENERATED]: electronicInvoiceFamiliesEnum.TO_BE_GENERATED,
    [electronicInvoiceProgressStatusesEnum.GENERATED_XML]: electronicInvoiceFamiliesEnum.GENERATED,
    [electronicInvoiceProgressStatusesEnum.INTERNAL_TRANSMISSION_ERROR]: electronicInvoiceFamiliesEnum.GENERATED,
    [electronicInvoiceProgressStatusesEnum.SENT_TO_CONNECTOR]: electronicInvoiceFamiliesEnum.SENT,
    [electronicInvoiceProgressStatusesEnum.SENT_TO_SDI]: electronicInvoiceFamiliesEnum.SENT,
    [electronicInvoiceProgressStatusesEnum.NOT_YET_DELIVERED]: electronicInvoiceFamiliesEnum.SENT,
    [electronicInvoiceProgressStatusesEnum.REPROCESSED]: electronicInvoiceFamiliesEnum.SENT,
    [electronicInvoiceProgressStatusesEnum.PROCESSING_SDI]: electronicInvoiceFamiliesEnum.SENT,
    [electronicInvoiceProgressStatusesEnum.CONNECTOR_GENERIC_ERROR]: electronicInvoiceFamiliesEnum.ERROR,
    [electronicInvoiceProgressStatusesEnum.REJECTED_SDI]: electronicInvoiceFamiliesEnum.ERROR,
    [electronicInvoiceProgressStatusesEnum.REJECTED_PA]: electronicInvoiceFamiliesEnum.ERROR,
    [electronicInvoiceProgressStatusesEnum.SDI_GENERIC_ERROR]: electronicInvoiceFamiliesEnum.ERROR,
    [electronicInvoiceProgressStatusesEnum.FAILED_DELIVERY]: electronicInvoiceFamiliesEnum.NOT_DELIVERED,
    [electronicInvoiceProgressStatusesEnum.TAX_DRAWER]: electronicInvoiceFamiliesEnum.NOT_DELIVERED,
    [electronicInvoiceProgressStatusesEnum.DELIVERED]: electronicInvoiceFamiliesEnum.DELIVERED,
    [electronicInvoiceProgressStatusesEnum.MANUAL_DELIVERY]: electronicInvoiceFamiliesEnum.DELIVERED,
    [electronicInvoiceProgressStatusesEnum.DELIVERED_ACCEPTED_PA]: electronicInvoiceFamiliesEnum.DELIVERED,
    [electronicInvoiceProgressStatusesEnum.MANUAL_UPLOAD]: electronicInvoiceFamiliesEnum.DELIVERED,
    [electronicInvoiceProgressStatusesEnum.DELIVERED_EXPIRED_PA_TERMS]: electronicInvoiceFamiliesEnum.DELIVERED,
    [electronicInvoiceProgressStatusesEnum.MANUAL_LOCK]: electronicInvoiceFamiliesEnum.LOCKED,
    [electronicInvoiceProgressStatusesEnum.NUMBERING_LOCK]: electronicInvoiceFamiliesEnum.LOCKED,
    [electronicInvoiceProgressStatusesEnum.REASON_LOCK]: electronicInvoiceFamiliesEnum.LOCKED,
    [electronicInvoiceProgressStatusesEnum.COUNTRY_LOCK]: electronicInvoiceFamiliesEnum.LOCKED,
  })
  const electronicInvoiceSdiDocumentTypeEnum = Object.freeze({
    INVOICE: 'TD01',
    CREDIT_NOTE: 'TD04',
  })
  const electronicInvoiceUserActionsMap = Object.freeze({
    MANUAL_LOCK: { id: 1, families: [electronicInvoiceFamiliesEnum.NOT_GENERABLE, electronicInvoiceFamiliesEnum.TO_BE_GENERATED] },
    CANCEL_XML: { id: 2, families: [electronicInvoiceFamiliesEnum.GENERATED] },
    MANUAL_UPLOAD: { id: 3, families: [electronicInvoiceFamiliesEnum.GENERATED] },
    MANUAL_DELIVERY: { id: 4, families: [electronicInvoiceFamiliesEnum.NOT_DELIVERED] },
    REJECTED_PUBLIC_ADMINISTRATION: { id: 5, families: [electronicInvoiceFamiliesEnum.DELIVERED] },
    DOWNLOAD_XML: { id: 6 },
  })
  const environment = ref(null)
  const expenseTypes = ref([])
  const languages = ref([])
  const localizations = ref({})
  const printEntities = ref({ SUPPLIER_ORDER: 1, QUOTE: 2, WORKSHEET: 3, SALE_DOCUMENT: 4 })
  const printModels = ref([])
  const reloadKey = ref(0)
  const productAnalyticGroups = ref([])
  const productManufacturerFamilies = ref([])
  const documentPriceTypes = Object.freeze({ VAT: 0, NO_VAT: 1, CUSTOMER: 2 })
  const productTypologies = ref([])
  const purchaseFamilies = ref([])
  const hiddenPurchaseFamilyCodes = Object.freeze({ ROUNDING: 'AR', EXPENSE_PERCENTAGE: 'SP' })
  const roundingRowType = Object.freeze({ ROUNDING: 1, EXPENSE_PERCENTAGE: 2 })
  const roundingProductCode = 'ROUNDING'
  const saleDepartments = ref([])
  const selectedLanguage = ref(null)
  const settings = ref(null)
  const subjectContactTypes = Object.freeze({ CONTACT: 0, DELIVERY_ADDRESS: 1 })
  const technicalQualifications = ref([])
  const transportTypeEnum = Object.freeze({
    COURIER: 1,
    COMPANY_VEHICLES: 2,
    CUSTOMER: 3,
  })
  const validationTypes = Object.freeze({ ERROR: 0, WARNING: 1 })
  const vehicleTypes = ref([])
  const version = ref(null)

  const incrementReloadKey = () => {
    reloadKey.value++
  }

  const fetchVersionAsync = async() => {
    const res = await portalApi.getVersionAsync()
    const currentVersion = res.data
    environment.value = res.data.env
    const isVersionChanged = currentVersion.runtime_version !== version.value?.runtime_version || currentVersion.db_version !== version.value?.db_version

    if (isVersionChanged) {
      await fetchLanguagesAsync()
      version.value = { runtime_version: currentVersion.runtime_version, db_version: currentVersion.db_version }
    }
    await setLanguageAsync(selectedLanguage.value, isVersionChanged)
  }

  const fetchSettingsAsync = async() => {
    const res = await portalApi.getSettingsAsync()
    if (res.data) {
      settings.value = res.data
    }
  }

  const fetchLanguagesAsync = async() => {
    const res = await portalApi.getLanguagesAsync()
    if (res.data) {
      languages.value = res.data
    }
  }

  const fetchLocalizationsAsync = async(languageCode, forceUpdate = false) => {
    if ((!localizations.value[languageCode]) || localizations.value[languageCode].length === 0 || forceUpdate) {
      const res = await portalApi.getLocalizationsAsync(languageCode)
      if (res.data) {
        localizations.value[languageCode] = res.data
      }
    }
  }

  const fetchCatalogRowsAsync = async(catalogId) => portalApi.get(portalApi.catalogRowsUrl(catalogId))
  const fetchCatalogsAsync = async(force = false) => useGetOrFetchAsync(catalogs, () => portalApi.getCatalogsAsync(), force)
  const fetchElectronicInvoiceProgressStatusesAsync = async(force = false) => useGetOrFetchAsync(electronicInvoiceProgressStatuses, () => portalApi.getElectronicInvoiceProgressStatusesAsync(), force)
  const fetchElectronicInvoiceSdiDocumentTypesAsync = async(force = false) => useGetOrFetchAsync(electronicInvoiceSdiDocumentTypes, () => portalApi.getElectronicInvoiceSdiDocumentTypesAsync(), force)
  const fetchExpenseTypesAsync = async(force = false) => useGetOrFetchAsync(expenseTypes, () => portalApi.getExpenseTypesAsync(), force)
  const fetchLetterOfAppointmentAsync = async() => portalApi.getLetterOfAppointmentAsync()
  const fetchPrintModelsAsync = async(force = false) => useGetOrFetchAsync(printModels, () => portalApi.getPrintModelsAsync(), force)
  const fetchProductAnalyticGroupsAsync = async(force = false) => useGetOrFetchAsync(productAnalyticGroups, () => portalApi.getProductAnalyticGroupsAsync(), force)
  const fetchProductManufacturerFamiliesAsync = async(force = false) => useGetOrFetchAsync(productManufacturerFamilies, () => portalApi.getProductManufacturerFamiliesAsync(), force)
  const fetchProductTypologiesAsync = async(force = false) => useGetOrFetchAsync(productTypologies, () => portalApi.getProductTypologiesAsync(), force)
  const fetchPurchaseFamiliesAsync = async(force = false) => useGetOrFetchAsync(purchaseFamilies, () => portalApi.getPurchaseFamiliesAsync(), force)
  const fetchVehicleTypesAsync = async(force = false) => useGetOrFetchAsync(vehicleTypes, () => portalApi.getVehicleTypesAsync(), force)
  const fetchSaleDepartmentsAsync = async(force = false) => useGetOrFetchAsync(saleDepartments, () => portalApi.getSaleDepartmentsAsync(), force)
  const fetchTechnicalQualificationsAsync = async(force = false) => useGetOrFetchAsync(technicalQualifications, () => portalApi.getTechnicalQualificationsAsync(), force)

  const getLocalizedAppointmentNotifyEnums = () => getLocalizedItems(Object.keys(appointmentNotifyEnum).map(x => ({
    id: appointmentNotifyEnum[x],
    name: `common.${x.toLowerCase()}`,
  })))
  const getLocalizedAttachmentCategories = () => getLocalizedItems(Object.keys(attachmentCategoryEnum).map(x => ({
    id: attachmentCategoryEnum[x],
    name: `common.attachmentCategory.${x.toLowerCase()}`,
  })))
  const getLocalizedAttachmentSubcategories = () => getLocalizedItems(Object.keys(attachmentSubcategoryEnum).map(x => ({
    id: attachmentSubcategoryEnum[x],
    name: `common.attachmentSubcategory.${x.toLowerCase()}`,
  })))
  const getLocalizedAttachmentTags = () => {
    const colors = {
      reference_document: 'blue',
      privacy_authorization: 'green',
      other: 'grey',
      tax_code: 'orange',
      identity_document: 'teal',
      loyalty_card: 'purple',
      compliance: 'red',
      vehicle_photo: 'cyan',
      damage_photo: 'deep-orange',
      warranty: 'indigo',
      vehicle_registration_document: 'blue-grey',
      homologation: 'amber',
      optional_equipment: 'light-green',
      contract: 'deep-purple',
    }
    return getLocalizedItems(Object.keys(attachmentTagEnum).map(x => ({
      id: attachmentTagEnum[x],
      name: `common.attachmentTag.${x.toLowerCase()}`,
      color: colors[x.toLowerCase()],
    })))
  }
  const getLocalizedCatalogs = () => getTranslatedItems(catalogs.value)
  const getLocalizedDocumentStatuses = () => getLocalizedItems(documentStatuses.value)
  const getLocalizedDocumentTypes = () => getLocalizedItems(Object.keys(documentTypesEnum).map(x => {
    const colors = {
      sale_correspondent: '#2196f3', // Blue 500
      sale_ddt: '#4caf50', // Green 500
      sale_invoice: '#9c27b0', // Purple 500
      sale_immediate_invoice: '#3f51b5', // Indigo 500
      sale_fiscal_receipt: '#ff9800', // Orange 500
      sale_credit_note: '#f44336', // Red 500
      garage_quote: '#00bcd4', // Cyan 500
      garage_worksheet: '#009688', // Teal 500
      garage_event: '#e91e63', // Pink 500
      order_supplier_order: '#607d8b', // Blue Grey 500
    }
    const id = documentTypesEnum[x]
    return {
      id,
      name: `common.documentType.${x.toLowerCase()}`,
      nameAlt: `common.documentTypeAlt.${x.toLowerCase()}`,
      color: colors[x.toLowerCase()],
      page: documentTypePageMap[id] ?? null,
    }
  }))
  const getDocumentTypeName = (id) => getLocalizedDocumentTypes().find(i => i.id === id)?.name ?? ''
  const getDocumentTypeNameAlt = (id) => getLocalizedDocumentTypes().find(i => i.id === id)?.nameAlt ?? ''
  const getLocalizedElectronicInvoiceFamilies = () => getLocalizedItems(Object.keys(electronicInvoiceFamiliesEnum).map(x => {
    const colors = {
      not_generable: '#B04646',
      to_be_generated: '#3B8D3B',
      generated: '#777777',
      sent: '#5C58A0',
      error: '#C1507B',
      not_delivered: '#E1903B',
      delivered: '#3BAE3B',
      locked: '#555555',
    }
    return {
      id: electronicInvoiceFamiliesEnum[x],
      name: `common.electronicInvoiceFamilyType.${x.toLowerCase()}`,
      nameAlt: `common.electronicInvoiceFamilyTypeAlt.${x.toLowerCase()}`,
      color: colors[x.toLowerCase()],
    }
  }))
  const getLocalizedElectronicInvoiceProgressStatuses = () => getLocalizedItems(Object.entries(electronicInvoiceProgressStatusesEnum).map(([key, value]) => ({
    id: value,
    name: `common.electronicInvoiceProgressStatusType.${key.toLowerCase()}`,
    family: getElectronicInvoiceFamilyByProgressStatus(value),
  }))
  )
  const getLocalizedElectronicInvoiceSdiDocumentTypes = () => getTranslatedItems(electronicInvoiceSdiDocumentTypes.value)
  const getLocalizedExpenseTypes = () => getTranslatedItems(expenseTypes.value)
  const getLocalizedLanguages = () => getTranslatedItems(languages.value)
  const getLocalizedPrintModels = () => getTranslatedItems(printModels.value)
  const getLocalizedProductAnalyticGroups = () => getTranslatedItems(productAnalyticGroups.value)
  const getLocalizedProductManufacturerFamilies = () => getTranslatedItems(productManufacturerFamilies.value)
  const getLocalizedDocumentPriceTypes = () => getLocalizedItems(Object.keys(documentPriceTypes).map(x => ({
    id: documentPriceTypes[x],
    name: `common.documentPriceType.${x.toLowerCase()}`,
  })))
  const getLocalizedDocumentGenerateTypes = () => getLocalizedItems(Object.keys(documentGenerateEnum).map(x => ({
    id: documentGenerateEnum[x],
    name: `common.${x.toLowerCase()}`,
  })))
  const getLocalizedDocumentGeneratingTypes = () => getLocalizedItems(Object.keys(documentGeneratingEnum).map(x => ({
    id: documentGeneratingEnum[x],
    name: `common.${x.toLowerCase()}`,
  })))
  const getLocalizedProductTypologies = () => getTranslatedItems(productTypologies.value)
  const getLocalizedPurchaseFamilies = () => getTranslatedItems(purchaseFamilies.value).filter(({ code }) => !Object.values([hiddenPurchaseFamilyCodes.ROUNDING]).includes(code))
  const getLocalizedShippingCostTypes = () => getLocalizedItems(Object.keys(shippingCostTypeEnum).map(x => ({
    id: shippingCostTypeEnum[x],
    name: `common.shippingCostTypeEnum.${x.toLowerCase()}`,
  })))
  const getLocalizedSubjectContactTypes = () => getLocalizedItems(Object.keys(subjectContactTypes).map(x => ({
    id: subjectContactTypes[x],
    name: `common.subjectContactTypes.${x.toLowerCase()}`
  })))
  const getLocalizedTechnicalQualifications = () => getTranslatedItems(technicalQualifications.value)
  const getLocalizedTransportTypes = () => getLocalizedItems(Object.keys(transportTypeEnum).map(x => ({
    id: transportTypeEnum[x],
    name: `common.transportType.${x.toLowerCase()}`
  })))
  const getLocalizedVehicleTypes = () => getTranslatedItems(vehicleTypes.value)
  const getLocalizedSaleDepartments = () => getTranslatedItems(saleDepartments.value)

  const getElectronicInvoiceFamilyByProgressStatus = (status) => electronicInvoiceProgressStatusesMap[status] ?? null
  const getPurchaseFamilyById = (id) => purchaseFamilies.value.find(x => x.id === id)

  const setLanguageAsync = async(languageCode, forceUpdate = false) => {
    const currentLanguageCode = navigator.language.toLowerCase()

    languageCode = languageCode?.code || languageCode || currentLanguageCode

    const availableLanguageCodes = languages.value.map(l => l.code.toLowerCase())

    if (!availableLanguageCodes.includes(languageCode)) {
      const availableLanguage = availableLanguageCodes.find(l => l.startsWith(languageCode))
      languageCode = availableLanguage?.code || window.config.DEFAULT_LANGUAGE
    }

    if (languageCode !== window.config.DEFAULT_LANGUAGE) {
      await fetchLocalizationsAsync(window.config.DEFAULT_LANGUAGE, forceUpdate) // Always fetch DEFAULT_LANGUAGE localizations to have a fallback for missing translations
    }
    await fetchLocalizationsAsync(languageCode, forceUpdate)
    await updateLocaleAsync(languageCode, toRaw(localizations.value))
    selectedLanguage.value = languageCode
  }

  const $reset = () => {
    catalogs.value = []
    customerPrivacyModules.value = []
    expenseTypes.value = []
    languages.value = []
    localizations.value = {}
    printModels.value = []
    productAnalyticGroups.value = []
    productManufacturerFamilies.value = []
    productTypologies.value = []
    purchaseFamilies.value = []
    saleDepartments.value = []
    electronicInvoiceSdiDocumentTypes.value = []
    technicalQualifications.value = []
    version.value = null
    vehicleTypes.value = []
  }

  const localizationsReset = () => {
    localizations.value = {}
  }

  return {
    api,
    appointmentNotifyEnum,
    attachmentCategoryEnum,
    attachmentCategoryMap,
    attachmentSubcategoryEnum,
    attachmentTagEnum,
    attachmentTagMap,
    catalogs,
    documentPriceTypes,
    customerPrivacyModules,
    discountPropertyEnum,
    discountPriorityMasks,
    documentGenerateEnum,
    documentGeneratingEnum,
    documentStatuses,
    documentStatusesEnum,
    documentProgressStatusesEnum,
    documentTypesEnum,
    environment,
    expenseTypes,
    electronicInvoiceEditableFamilies,
    electronicInvoiceFamiliesEnum,
    electronicInvoiceProgressStatuses,
    electronicInvoiceProgressStatusesEnum,
    electronicInvoiceProgressStatusesMap,
    electronicInvoiceSdiDocumentTypeEnum,
    electronicInvoiceUserActionsMap,
    languages,
    localizations,
    roundingRowType,
    roundingProductCode,
    productAnalyticGroups,
    productManufacturerFamilies,
    productTypologies,
    purchaseFamilies,
    hiddenPurchaseFamilyCodes,
    printEntities,
    printModels,
    reloadKey,
    saleDepartments,
    shippingCostTypeEnum,
    electronicInvoiceSdiDocumentTypes,
    documentTypePageMap,
    selectedLanguage,
    settings,
    subjectValidationTypesEnum,
    subjectContactTypes,
    technicalQualifications,
    transportTypeEnum,
    validationTypes,
    vehicleTypes,
    version,
    $reset,
    fetchCatalogRowsAsync,
    fetchCatalogsAsync,
    fetchElectronicInvoiceProgressStatusesAsync,
    fetchElectronicInvoiceSdiDocumentTypesAsync,
    fetchExpenseTypesAsync,
    fetchLanguagesAsync,
    fetchLetterOfAppointmentAsync,
    fetchLocalizationsAsync,
    fetchPrintModelsAsync,
    fetchProductAnalyticGroupsAsync,
    getLocalizedDocumentPriceTypes,
    getLocalizedDocumentGenerateTypes,
    getLocalizedDocumentGeneratingTypes,
    fetchProductManufacturerFamiliesAsync,
    fetchProductTypologiesAsync,
    fetchPurchaseFamiliesAsync,
    fetchSaleDepartmentsAsync,
    fetchTechnicalQualificationsAsync,
    fetchSettingsAsync,
    fetchVersionAsync,
    fetchVehicleTypesAsync,
    getLocalizedAppointmentNotifyEnums,
    getLocalizedProductAnalyticGroups,
    getLocalizedAttachmentCategories,
    getLocalizedAttachmentSubcategories,
    getLocalizedAttachmentTags,
    getLocalizedCatalogs,
    getLocalizedDocumentStatuses,
    getLocalizedDocumentTypes,
    getDocumentTypeName,
    getDocumentTypeNameAlt,
    getLocalizedElectronicInvoiceFamilies,
    getLocalizedElectronicInvoiceProgressStatuses,
    getLocalizedElectronicInvoiceSdiDocumentTypes,
    getLocalizedExpenseTypes,
    getLocalizedLanguages,
    getLocalizedProductManufacturerFamilies,
    getLocalizedPurchaseFamilies,
    getLocalizedPrintModels,
    getLocalizedSubjectContactTypes,
    getLocalizedTechnicalQualifications,
    getLocalizedTransportTypes,
    getLocalizedProductTypologies,
    getLocalizedSaleDepartments,
    getLocalizedShippingCostTypes,
    getLocalizedVehicleTypes,
    getElectronicInvoiceFamilyByProgressStatus,
    getPurchaseFamilyById,
    incrementReloadKey,
    localizationsReset,
    setLanguageAsync,
  }
}, {
  persist: [
    {
      pick: [
        'catalogs',
        'electronicInvoiceProgressStatuses',
        'expenseTypes',
        'languages',
        'localizations',
        'printModels',
        'productAnalyticGroups',
        'productManufacturerFamilies',
        'productTypologies',
        'purchaseFamilies',
        'selectedLanguage',
        'version',
        'vehicleTypes',
        'saleDepartments',
        'electronicInvoiceSdiDocumentTypes',
        'technicalQualifications',
      ],
      storage: localStorage
    }
  ]
})
